import React, { useState, useEffect } from "react";
import {
  Calendar as CalendarIcon,
  MessageSquare,
  X,
  RefreshCw,
  AlertCircle,
  Phone,
  Info,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Clock,
  Check,
  Facebook,
  Instagram,
  Mail
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { DateTime } from "luxon";
import { Pharmacy, Shift, WhatsAppLog, DiagResponse } from "./types";
import CartelesPage from "./admin/CartelesPage";
import PlantillasPage from "./admin/PlantillasPage";
import { EmailInbox } from "./components/EmailInbox";
import { TurnosPage } from "./admin/TurnosPage";
import { BotPage } from "./admin/BotPage";
import { adminFetch } from "./api";
import { AdminShell } from "./admin/AdminShell";
import { useAdminTheme } from "./admin/theme";
import { ToastProvider } from "./admin/ui";
import { type AdminView, canSee, getAdminRole } from "./admin/views";
import { Login, type LoginStatus } from "./admin/Login";
import { FarmaciasPage } from "./admin/FarmaciasPage";
import { AccesosPage } from "./admin/AccesosPage";
import { ConfigPage } from "./admin/ConfigPage";
import { PortadaPage } from "./admin/PortadaPage";
import { DEFAULT_FRONTPAGE, type FrontpageForm } from "./admin/frontpageDefaults";

// Load Firebase Client libraries
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged } from "firebase/auth";
import firebaseConfig from "../firebase-applet-config.json";

// Initialize Client-side Firebase safely
const clientApp =
  getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

function getSafeAuth() {
  try {
    return getAuth(clientApp);
  } catch (e: any) {
    console.warn(
      "Could not getAuth directly, possibly due to sandboxed iframe. Falling back...",
      e,
    );
    // Return a dummy object if auth completely fails, or let signInWithPopup fail later
    return null;
  }
}

interface GoogleUser {
  email: string;
  name: string;
  picture?: string;
}

// Geolocation and distance utility functions for Tandil
function calculateHaversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d;
}

// Safe LocalStorage helpers to avoid "The operation is insecure" or SecurityError in sandboxed/third-party contexts
function safeGetLocalStorage(key: string): string | null {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return localStorage.getItem(key);
    }
  } catch (e) {
    console.warn("localStorage is not accessible:", e);
  }
  return null;
}

function safeSetLocalStorage(key: string, value: string): void {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem(key, value);
    }
  } catch (e) {
    console.warn("localStorage is not accessible:", e);
  }
}

function safeRemoveLocalStorage(key: string): void {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.warn("localStorage is not accessible:", e);
  }
}

async function geocodeAddress(
  direccion: string,
  nombre: string,
): Promise<{ lat: number; lng: number } | null> {
  const cacheKey = `geo_pharmacy_${direccion.toLowerCase().replace(/[^a-z0-9]/g, "_")}`;
  if (typeof window !== "undefined") {
    const cached = safeGetLocalStorage(cacheKey);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        // ignore
      }
    }
  }

  try {
    const query = `${direccion}, Tandil, Argentina`;
    const res = await fetch(`/api/geocode?q=${encodeURIComponent(query)}`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.length > 0) {
        const coords = {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
        };
        if (typeof window !== "undefined") {
          safeSetLocalStorage(cacheKey, JSON.stringify(coords));
        }
        return coords;
      }
    }
  } catch (err) {
    console.error("Geocoding failed for:", direccion, err);
  }
  
  // Fallback to random coordinate close to Tandil if Nominatim fails
  return {
    lat: -37.32 + (Math.random() * 0.02 - 0.01),
    lng: -59.13 + (Math.random() * 0.02 - 0.01)
  };
}

export default function App() {
  // Sesión de administrador: la da Firebase Auth, no localStorage (P-9).
  // Así el panel solo se ve si hay un token real, el mismo que usa adminFetch().
  const [currentUser, setCurrentUser] = useState<GoogleUser | null>(null);
  const [authReady, setAuthReady] = useState<boolean>(false);
  const [signingIn, setSigningIn] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    // La sesión vieja guardada a mano ya no se usa: se borra si quedó.
    safeRemoveLocalStorage("tandil_admin_user");
    const auth = getSafeAuth();
    if (!auth) {
      setAuthReady(true);
      return;
    }
    return onAuthStateChanged(auth, (user) => {
      setCurrentUser(
        user?.email
          ? {
              email: user.email,
              name: user.displayName || user.email.split("@")[0],
              picture: user.photoURL || undefined,
            }
          : null,
      );
      setAuthReady(true);
    });
  }, []);

  // Ingresar con Google. Sin respaldo con prompt(): si Google no abre, se
  // muestra el error y no se entra (P-9). La sesión la toma onAuthStateChanged.
  const handleGoogleSignIn = async () => {
    setLoginError(null);
    const auth = getSafeAuth();
    if (!auth) {
      setLoginError("Este navegador no permite ingresar con Google. Probá desde otro navegador.");
      return;
    }
    setSigningIn(true);
    try {
      // Si había una cuenta sin permiso, se cierra para poder elegir otra.
      if (auth.currentUser) await auth.signOut();
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      await signInWithPopup(auth, provider);
    } catch (err: any) {
      const code = err?.code || "";
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        // la persona cerró la ventana: no es un error
      } else if (code === "auth/popup-blocked") {
        setLoginError("El navegador bloqueó la ventana de Google. Permití las ventanas emergentes para este sitio y probá de nuevo.");
      } else if (code === "auth/unauthorized-domain") {
        setLoginError(`El dominio ${window.location.hostname} no está autorizado en Firebase. Avisale al superadmin.`);
      } else {
        setLoginError("No pudimos abrir Google. Probá de nuevo en un momento.");
      }
    } finally {
      setSigningIn(false);
    }
  };

  // Navigation & View Status
  const [currentYear, setCurrentYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [currentMonth, setCurrentMonth] = useState<number>(
    new Date().getMonth() + 1,
  ); // 1-indexed (Jan = 1)

  // App data state
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [diagData, setDiagData] = useState<DiagResponse | null>(null);

  // Configuración de la portada; los valores de fábrica están en src/admin/frontpageDefaults.ts
  const [frontpageConfig, setFrontpageConfig] = useState<FrontpageForm>(DEFAULT_FRONTPAGE);
  // la pantalla de Portada avisa si tiene cambios sin guardar (para preguntar al salir)
  const [portadaDirty, setPortadaDirty] = useState<boolean>(false);

  // Community View state
  const [viewingNextShift, setViewingNextShift] = useState(false);
  const [carouselStartIndex, setCarouselStartIndex] = useState<number>(0);
  const [carouselDirection, setCarouselDirection] = useState<"next" | "prev">(
    "next",
  );
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  // Geolocation & Distance States for Tandil
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [distanceByPharmId, setDistanceByPharmId] = useState<{
    [id: string]: number;
  }>({});
  const [loadingLocation, setLoadingLocation] = useState<boolean>(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setCarouselStartIndex(0);
    setCarouselDirection("next");
  }, [viewingNextShift]);

  const [timeLeft, setTimeLeft] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
  }>({ hours: 0, minutes: 0, seconds: 0 });
  const [currentClockTime, setCurrentClockTime] = useState<string>("");

  // UI States
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  // Vistas del panel (§4.5): "public" es la web; el resto, AdminView.
  const [activeTab, setActiveTab] = useState<"public" | AdminView>("public");

  // Tema del panel (§4.3): la clase "dark" va en el div .admin, no en <html>.
  const { theme: adminTheme, toggleTheme: toggleAdminTheme } = useAdminTheme();

  // Rol del usuario logueado (§4.6). null = sin sesión o sin permiso.
  const adminRole = getAdminRole(currentUser?.email, frontpageConfig?.adminEmail);

  // Si no llegó la configuración de la portada, Accesos (y la Ficha 9, Portada) no deja
  // guardar: guardaría los valores de fábrica encima de lo publicado.
  const [frontpageLoadFailed, setFrontpageLoadFailed] = useState<boolean>(false);

  // Correos sin leer para el menú. Solo el superadmin puede leer /api/emails; la
  // pantalla de Correos lo mantiene al día con onUnreadChange.
  const [unreadEmails, setUnreadEmails] = useState<number>(0);
  useEffect(() => {
    if (adminRole !== "super") return;
    let cancelled = false;
    adminFetch("/api/emails")
      .then((res) => (res.ok ? res.json() : []))
      .then((list: Array<{ read?: boolean }>) => {
        if (!cancelled) setUnreadEmails(list.filter((e) => !e.read).length);
      })
      .catch(() => {
        // el número del menú es orientativo: el error se ve al abrir Correos
      });
    return () => {
      cancelled = true;
    };
  }, [adminRole]);

  // El admin de turnos solo puede estar en Turnos y Farmacias: cualquier otra
  // vista lo devuelve a Turnos (§4.6).
  useEffect(() => {
    if (adminRole && activeTab !== "public" && !canSee(adminRole, activeTab)) {
      setActiveTab("turnos");
    }
  }, [adminRole, activeTab]);

  // Listen to popstate or hashchange to enter /admin automatically
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleUrlChange = () => {
      const path = window.location.pathname;
      const hash = window.location.hash;
      if (path === "/admin" || hash === "#/admin" || hash === "#admin") {
        if (activeTab === "public") {
          setActiveTab("turnos");
        }
      }
    };
    handleUrlChange();
    window.addEventListener("popstate", handleUrlChange);
    window.addEventListener("hashchange", handleUrlChange);
    return () => {
      window.removeEventListener("popstate", handleUrlChange);
      window.removeEventListener("hashchange", handleUrlChange);
    };
  }, [activeTab]);

  const navigateAdmin = (view: AdminView) => {
    // Portada con cambios sin guardar: se pregunta antes de salir (Ficha 9)
    if (
      portadaDirty &&
      activeTab === "cfg:portada" &&
      view !== "cfg:portada" &&
      !window.confirm("Hay cambios sin guardar en la portada. ¿Salir sin guardarlos?")
    ) {
      return;
    }
    setActiveTab(view);
    setSelectedDate(null);
  };

  const handleAdminLogout = () => {
    getSafeAuth()?.signOut().catch(() => {});
    setCurrentUser(null);
    setActiveTab("public");
  };

  // Regenerar la caché pública. Sin botón hasta la Ficha 8 (Accesos →
  // Mantenimiento), que la llama y muestra guardando/listo/error con useToast.
  const regenerateCache = async (): Promise<void> => {
    const res = await adminFetch("/api/cache/invalidate", { method: "POST" });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `HTTP ${res.status}`);
    }
  };

  // Modal del Excel del mes (lo abre la pantalla de Turnos) y si falló la última carga de datos
  const [showOriginalExcelUploader, setShowOriginalExcelUploader] = useState(false);
  const [shiftsLoadFailed, setShiftsLoadFailed] = useState<boolean>(false);

  // Edit Shift states
  const [selectedDate, setSelectedDate] = useState<string | null>(null);


  // Helper phone sanitizer
  const sanitizePhoneNumber = (num: any): string => {
    if (!num) return "";
    return String(num).replace(/[^0-9]/g, "");
  };

  // Safe client calculations for Argentina timezone
  const getActiveShiftDateClient = (offsetDays = 0): string => {
    let localNow = DateTime.now().setZone("America/Argentina/Buenos_Aires");
    if (localNow.hour < 8 || (localNow.hour === 8 && localNow.minute < 30)) {
      localNow = localNow.minus({ days: 1 });
    }
    if (offsetDays !== 0) {
      localNow = localNow.plus({ days: offsetDays });
    }
    return localNow.toFormat("yyyy-MM-dd");
  };

  // Geolocation and navigation trigger functions
  const handleCalculateClosest = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setLocationError(
        "Tu dispositivo o navegador no soporta la Geolocalización.",
      );
      return;
    }
    setLoadingLocation(true);
    setLocationError(null);

    try {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;

          // Identify currently active pharmacies to geocode
          const activePharmIds = viewingNextShift
            ? shifts.find((s) => s.date === getActiveShiftDateClient(1))
                ?.pharmacyIds || []
            : shifts.find((s) => s.date === getActiveShiftDateClient(0))
                ?.pharmacyIds || [];
          const activePharmList = pharmacies.filter((p) =>
            activePharmIds.includes(p.id),
          );

          const updatedDistances: { [id: string]: number } = {};
          for (let i = 0; i < activePharmList.length; i++) {
            const pharm = activePharmList[i];
            if (i > 0) {
              // Respect Nominatim's 1 request per second policy
              await new Promise(resolve => setTimeout(resolve, 1100));
            }
            const coords = await geocodeAddress(pharm.direccion, pharm.nombre);
            if (coords) {
              const dist = calculateHaversineDistance(
                userLat,
                userLng,
                coords.lat,
                coords.lng,
              );
              updatedDistances[pharm.id] = parseFloat(dist.toFixed(2));
            }
          }

          // Wait to update the UI until ALL calculations are done
          setUserLocation({ lat: userLat, lng: userLng });
          setDistanceByPharmId(updatedDistances);
          setLoadingLocation(false);
        },
        (error) => {
          console.error("Error getting geolocation:", error);
          let msg =
            "No pudimos acceder a tu ubicación. Asegurate de habilitar los permisos en tu navegador.";
          if (error.code === error.PERMISSION_DENIED) {
            msg =
              "Permiso denegado. Por favor, habilitá el acceso a la ubicación en la configuración de la página.";
          }
          setLocationError(msg);
          setLoadingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
      );
    } catch (err: any) {
      console.error("Security/Geolocation error:", err);
      setLocationError("Operación denegada por seguridad (ej. iframe bloqueado). Intenta abrir la app en una nueva pestaña.");
      setLoadingLocation(false);
    }
  };

  const handleClearLocation = () => {
    setUserLocation(null);
    setDistanceByPharmId({});
    setLocationError(null);
  };

  useEffect(() => {
    if (userLocation) {
      const updateActiveDistances = async () => {
        const activePharmIds = viewingNextShift
          ? shifts.find((s) => s.date === getActiveShiftDateClient(1))
              ?.pharmacyIds || []
          : shifts.find((s) => s.date === getActiveShiftDateClient(0))
              ?.pharmacyIds || [];
        const activePharmList = pharmacies.filter((p) =>
          activePharmIds.includes(p.id),
        );

        const updatedDistances = { ...distanceByPharmId };
        let updated = false;

        for (const pharm of activePharmList) {
          if (updatedDistances[pharm.id] === undefined) {
            const coords = await geocodeAddress(pharm.direccion, pharm.nombre);
            if (coords) {
              const dist = calculateHaversineDistance(
                userLocation.lat,
                userLocation.lng,
                coords.lat,
                coords.lng,
              );
              updatedDistances[pharm.id] = parseFloat(dist.toFixed(2));
              updated = true;
            }
          }
        }

        if (updated) {
          setDistanceByPharmId(updatedDistances);
        }
      };
      updateActiveDistances();
    }
  }, [viewingNextShift, userLocation, pharmacies, shifts]);

  // Load backend data
  const fetchData = async () => {
    setRefreshing(true);
    try {
      // Las tres consultas en paralelo; las fotos llegan aparte como links /foto/...
      const [phRes, shRes, fpRes] = await Promise.all([
        fetch("/api/pharmacies"),
        fetch("/api/shifts"),
        fetch("/api/frontpage/config"),
      ]);
      if (phRes.ok) {
        const phData = await phRes.json();
        setPharmacies(phData);
      }

      if (shRes.ok) {
        const shData = await shRes.json();
        setShifts(shData);
      }

      if (fpRes.ok) {
        const fpData = await fpRes.json();
        setFrontpageConfig(fpData);
      }
      setFrontpageLoadFailed(!fpRes.ok);

      // Turnos: si no llegaron farmacias o turnos, la pantalla lo avisa en vez de mostrar el mes vacío
      setShiftsLoadFailed(!phRes.ok || !shRes.ok);
      return phRes.ok && shRes.ok;
    } catch (err) {
      console.error("Failed to sync client data from server:", err);
      setShiftsLoadFailed(true);
      setFrontpageLoadFailed(true);
      return false;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Google Analytics script injection
  useEffect(() => {
    const gaId = frontpageConfig?.googleAnalyticsId;
    if (!gaId || gaId.trim() === "") return;

    // Check if gtag script already loaded
    const scriptId = "google-analytics-script";
    let existingScript = document.getElementById(scriptId);

    if (!existingScript) {
      // 1. Create main script load
      const gaScript = document.createElement("script");
      gaScript.id = scriptId;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      gaScript.async = true;
      document.head.appendChild(gaScript);

      // 2. Create inline script initialization
      const inlineScript = document.createElement("script");
      inlineScript.id = "google-analytics-inline-script";
      inlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${gaId}', { page_path: window.location.pathname });
      `;
      document.head.appendChild(inlineScript);
    }
  }, [frontpageConfig?.googleAnalyticsId]);

  useEffect(() => {
    fetchData();

    // High precision Argentina Countdown to next 08:30 AM
    const updateCountdown = () => {
      const now = DateTime.now().setZone("America/Argentina/Buenos_Aires");
      let target = now.set({ hour: 8, minute: 30, second: 0, millisecond: 0 });
      if (now > target) {
        target = target.plus({ days: 1 });
      }
      const diff = target.diff(now, ["hours", "minutes", "seconds"]).toObject();
      setTimeLeft({
        hours: Math.max(0, Math.floor(diff.hours || 0)),
        minutes: Math.max(0, Math.floor(diff.minutes || 0)),
        seconds: Math.max(0, Math.floor(diff.seconds || 0)),
      });
      setCurrentClockTime(now.toFormat("HH:mm:ss"));
    };
    updateCountdown();
    const countdownTimer = setInterval(updateCountdown, 1000);

    return () => {
      clearInterval(countdownTimer);
    };
  }, []);

  // Diagnóstico de WhatsApp: solo con un administrador logueado, y con sondeo
  // periódico solo en Configuración → Bot. (Antes lo consultaban TODOS los visitantes
  // cada 4 s, lo que agotaba la cuota diaria de lecturas de Firestore.)
  // El error queda visible en la pantalla del bot (§4.8).
  const [diagError, setDiagError] = useState<string | null>(null);
  const loadDiag = async (): Promise<void> => {
    try {
      const dgRes = await adminFetch("/api/whatsapp/diag");
      if (!dgRes.ok) {
        const data = await dgRes.json().catch(() => ({}));
        throw new Error(data.error || `El servidor respondió ${dgRes.status}.`);
      }
      setDiagData(await dgRes.json());
      setDiagError(null);
    } catch (err: any) {
      setDiagError(err?.message || "Sin conexión con el servidor.");
    }
  };
  useEffect(() => {
    if (!currentUser) return;
    loadDiag();
    if (activeTab !== "cfg:bot") return;
    const interval = setInterval(loadDiag, 15000);
    return () => clearInterval(interval);
  }, [currentUser, activeTab]);

  const handlePrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Llamada del panel al servidor que avisa si falla (P-13). adminFetch ya trae en
  // castellano la sesión vencida (401) y la falta de permiso (403); acá se suman la
  // red caída y cualquier otra respuesta que no sea 2xx.
  const adminRequest = async (
    input: string,
    init: RequestInit,
    fallback: string,
  ): Promise<Response> => {
    let res: Response;
    try {
      res = await adminFetch(input, init);
    } catch (err) {
      if (err instanceof TypeError) {
        throw new Error("No hay conexión con el servidor. Revisá internet y reintentá.");
      }
      throw err;
    }
    if (!res.ok) {
      let message = fallback;
      try {
        const data = await res.json();
        if (data && data.error) message = data.error;
      } catch (_) {}
      throw new Error(message);
    }
    return res;
  };

  // Agrega una farmacia a un día. Si no se guarda, lanza el motivo (lo muestra el cajón del día).
  const handleAddPharmacyToDate = async (date: string, pharmacyId: string) => {
    const current = shifts.find((s) => s.date === date)?.pharmacyIds || [];
    if (current.includes(pharmacyId)) return; // already added
    const pharmacyIds = [...current, pharmacyId];
    await adminRequest(
      "/api/shifts",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date, pharmacyIds }),
      },
      "El servidor no guardó el cambio.",
    );
    setShifts((prev) => [...prev.filter((s) => s.date !== date), { date, pharmacyIds }]);
  };

  // Quita una farmacia de un día; si era la última, borra el día. Lanza el motivo si falla.
  const handleRemovePharmacyFromDate = async (date: string, pharmacyId: string) => {
    const current = shifts.find((s) => s.date === date)?.pharmacyIds || [];
    const pharmacyIds = current.filter((id) => id !== pharmacyId);
    if (pharmacyIds.length === 0) {
      await adminRequest(`/api/shifts/${date}`, { method: "DELETE" }, "El servidor no borró el día.");
      setShifts((prev) => prev.filter((s) => s.date !== date));
    } else {
      await adminRequest(
        "/api/shifts",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ date, pharmacyIds }),
        },
        "El servidor no guardó el cambio.",
      );
      setShifts((prev) => prev.map((s) => (s.date === date ? { date, pharmacyIds } : s)));
    }
  };

  // Borra todos los turnos del mes. Lanza el motivo si falla (lo muestra el modal de Limpiar mes).
  const handleClearMonth = async (year: number, month: number) => {
    const yearMonth = `${year}-${String(month).padStart(2, "0")}`;
    await adminRequest(
      `/api/shifts/month/${yearMonth}`,
      { method: "DELETE" },
      "El servidor no borró el mes.",
    );
    setShifts((prev) => prev.filter((s) => !s.date.startsWith(`${yearMonth}-`)));
    setSelectedDate(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-850 font-sans selection:bg-emerald-500 selection:text-white">
      {/* Dynamic Community Frontpage conditional portal */}
      {activeTab === "public" &&
        (() => {
          // Typography maps
          const sizeMap: Record<string, string> = {
            xs: "text-[11px]",
            sm: "text-xs",
            base: "text-sm",
            lg: "text-base",
            xl: "text-lg",
          };
          const weightMap: Record<string, string> = {
            normal: "font-normal",
            medium: "font-medium",
            semibold: "font-semibold",
            bold: "font-bold",
            extrabold: "font-extrabold",
          };
          const colorMap: Record<string, string> = {
            white: "text-white",
            "slate-900": "text-slate-900",
            "slate-800": "text-slate-800",
            "emerald-900": "text-[#064e3b]",
            "yellow-300": "text-[#d97706]",
          };

          const fontSizeClass =
            sizeMap[frontpageConfig.pharmacyFontSize] || "text-sm";
          const fontWeightClass =
            weightMap[frontpageConfig.pharmacyFontWeight] || "font-extrabold";
          const fontColorClass =
            colorMap[frontpageConfig.pharmacyFontColor] || "text-white";

          const addressFontSizeClass =
            sizeMap[frontpageConfig.addressFontSize || ""] || "text-xs";
          const addressFontWeightClass =
            weightMap[frontpageConfig.addressFontWeight || ""] ||
            "font-semibold";
          const addressFontColorClass =
            colorMap[frontpageConfig.addressFontColor || ""] || "text-white";

          const phoneFontSizeClass =
            sizeMap[frontpageConfig.phoneFontSize || ""] || "text-xs";
          const phoneFontWeightClass =
            weightMap[frontpageConfig.phoneFontWeight || ""] || "font-semibold";
          const textPhoneColorClass =
            colorMap[frontpageConfig.phoneFontColor || ""] || "text-white";

          const gridColsClass =
            frontpageConfig.pharmaciesPerRow === 1
              ? "grid-cols-1 max-w-xl mx-auto"
              : frontpageConfig.pharmaciesPerRow === 3
                ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                : frontpageConfig.pharmaciesPerRow === 4
                  ? "grid-cols-1 md:grid-cols-3 lg:grid-cols-4"
                  : "grid-cols-1 md:grid-cols-2"; // default: 2

          const detailsColor = frontpageConfig.detailsBoxColor || "#10b981";

          // Calculate global WhatsApp link
          let globalWaPhone = "";
          if (frontpageConfig.whatsAppConsultPhone) {
            globalWaPhone = sanitizePhoneNumber(
              frontpageConfig.whatsAppConsultPhone,
            );
          } else {
            globalWaPhone = diagData?.config?.WHATSAPP_PHONE_NUMBER_ID
              ? sanitizePhoneNumber(diagData.config.WHATSAPP_PHONE_NUMBER_ID)
              : "";
          }
          const globalWaDefaultText =
            frontpageConfig.whatsAppConsultDefaultText || "Hoy";
          const globalWaLink = `https://wa.me/${globalWaPhone}?text=${encodeURIComponent(globalWaDefaultText)}`;

          // WhatsApp promotional banner template
          const renderWhatsAppPromoBanner = () => {
            if (loading || !frontpageConfig.showWhatsAppBanner) return null;

            return (
              <div className="w-full flex flex-col items-center justify-center text-center px-4 py-6 bg-transparent">
                <p className="text-sm md:text-base font-semibold text-slate-700 leading-relaxed max-w-2xl">
                  {frontpageConfig.whatsAppBannerText ||
                    "📲 Consulta quién está de guardia hoy o mañana cómodamente por WhatsApp."}
                </p>
                <div className="mt-3">
                  <a
                    href={globalWaLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white rounded-full text-xs font-extrabold transition-all shadow-md transform hover:scale-103 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MessageSquare size={13} />
                    Chatear por WhatsApp
                  </a>
                </div>
              </div>
            );
          };

          const renderTopBanner = () => {
            if (
              frontpageConfig.hideTopBannerMobile &&
              frontpageConfig.hideTopBannerDesktop
            ) {
              return null;
            }

            const hasImage =
              !!frontpageConfig.topBannerImage ||
              !!frontpageConfig.topBannerMobileImage;
            const desktopImg = frontpageConfig.topBannerImage;
            const mobileImg =
              frontpageConfig.topBannerMobileImage || desktopImg;

            let visibilityClass = "flex";
            if (frontpageConfig.hideTopBannerMobile) {
              visibilityClass = "hidden md:flex";
            } else if (frontpageConfig.hideTopBannerDesktop) {
              visibilityClass = "flex md:hidden";
            }

            return (
              <div
                onClick={() => {
                  if (frontpageConfig.topBannerLink) {
                    window.open(frontpageConfig.topBannerLink, "_blank");
                  }
                }}
                style={{
                  height: hasImage
                    ? "auto"
                    : `${frontpageConfig.topBannerHeight || 90}px`,
                  backgroundColor: hasImage
                    ? "transparent"
                    : frontpageConfig.topBannerBgColor || "#0f172a",
                  cursor: frontpageConfig.topBannerLink ? "pointer" : "default",
                }}
                className={`w-full items-center justify-center overflow-hidden relative border-0 ${visibilityClass}`}
              >
                {hasImage ? (
                  <picture className="w-full text-center block">
                    {frontpageConfig.topBannerMobileImage && (
                      <source
                        media="(max-width: 767px)"
                        srcSet={frontpageConfig.topBannerMobileImage}
                      />
                    )}
                    <img
                      src={desktopImg || mobileImg}
                      alt="Banner Superior"
                      referrerPolicy="no-referrer"
                      width="1200"
                      height={frontpageConfig.topBannerHeight || 90}
                      className="max-w-full h-auto object-contain mx-auto block border-0"
                      style={{
                        maxHeight: `${frontpageConfig.topBannerHeight || 90}px`,
                        aspectRatio: `1200/${frontpageConfig.topBannerHeight || 90}`,
                      }}
                    />
                  </picture>
                ) : (
                  <div
                    id="top-banner-content"
                    className="relative z-10 text-center px-4 py-2"
                  >
                    <h2 className="text-sm md:text-lg font-bold text-white tracking-wide truncate max-w-full drop-shadow-md">
                      {frontpageConfig.topBannerText ||
                        "Farmacias de Turno en Tandil"}
                    </h2>
                  </div>
                )}
              </div>
            );
          };

          const renderLocationLocator = () => {
            return (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs flex flex-col sm:flex-row justify-between items-center gap-4 text-slate-900">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600 shrink-0">
                    <MapPin
                      size={20}
                      className={loadingLocation ? "animate-bounce" : ""}
                    />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                      🧭 Ubicación y Distancias
                    </h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      {userLocation
                        ? "Calculando distancias en tiempo real desde tu posición. Las farmacias de guardia se ordenaron por cercanía."
                        : "Calculá la distancia real en KM a tu farmacia más cercana."}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-center sm:justify-end">
                  {userLocation ? (
                    <button
                      onClick={handleClearLocation}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer border border-slate-200"
                    >
                      <X size={13} />
                      Limpiar ubicación
                    </button>
                  ) : (
                    <button
                      onClick={handleCalculateClosest}
                      disabled={loadingLocation}
                      className="px-6 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm md:text-base font-extrabold rounded-2xl transition-all flex items-center gap-2 shadow-md hover:shadow-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-95"
                    >
                      {loadingLocation ? (
                        <>
                          <RefreshCw size={18} className="animate-spin" />
                          Localizando...
                        </>
                      ) : (
                        <>
                          <MapPin size={18} />
                          ¿Cuál me queda más cerca?
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          };

          const renderSecondBanner = () => {
            const hasImage =
              !!frontpageConfig.bottomBannerImage ||
              !!frontpageConfig.bottomBannerMobileImage;
            const hasText = !!frontpageConfig.bottomBannerText;
            if (!hasImage && !hasText) return null;

            const desktopImg = frontpageConfig.bottomBannerImage;
            const mobileImg =
              frontpageConfig.bottomBannerMobileImage || desktopImg;

            return (
              <div
                onClick={() => {
                  if (frontpageConfig.bottomBannerLink) {
                    window.open(frontpageConfig.bottomBannerLink, "_blank");
                  }
                }}
                style={{
                  height: hasImage
                    ? "auto"
                    : `${frontpageConfig.bottomBannerHeight || 170}px`,
                  backgroundColor: hasImage
                    ? "transparent"
                    : frontpageConfig.bottomBannerBgColor || "#1e293b",
                  cursor: frontpageConfig.bottomBannerLink
                    ? "pointer"
                    : "default",
                }}
                className="w-full flex items-center justify-center overflow-hidden relative mt-4 border-0"
              >
                {hasImage ? (
                  <picture className="w-full text-center block">
                    {frontpageConfig.bottomBannerMobileImage && (
                      <source
                        media="(max-width: 767px)"
                        srcSet={frontpageConfig.bottomBannerMobileImage}
                      />
                    )}
                    <img
                      src={desktopImg || mobileImg}
                      alt="Segundo Banner"
                      referrerPolicy="no-referrer"
                      width="1200"
                      height={frontpageConfig.bottomBannerHeight || 170}
                      className="max-w-full h-auto object-contain mx-auto block border-0"
                      style={{
                        maxHeight: `${frontpageConfig.bottomBannerHeight || 170}px`,
                        aspectRatio: `1200/${frontpageConfig.bottomBannerHeight || 170}`,
                      }}
                    />
                  </picture>
                ) : (
                  <div
                    id="second-banner-content"
                    className="relative z-10 text-center px-4 py-4"
                  >
                    <p className="text-sm md:text-base font-semibold text-white leading-relaxed mb-1 drop-shadow-sm">
                      {frontpageConfig.bottomBannerText}
                    </p>
                  </div>
                )}
              </div>
            );
          };

          const renderCountdownCard = () => {
            const containerStyle = frontpageConfig.hideCountdownFrame
              ? "bg-transparent border-none p-0 shadow-none flex flex-col justify-center items-center text-center gap-5 w-full mx-auto"
              : "bg-white rounded-2xl border border-slate-200 p-6 shadow-xs flex flex-col justify-center items-center text-center gap-6 w-full mx-auto";

            const itemBg = frontpageConfig.hideCountdownFrame
              ? "bg-slate-50 border border-slate-100 shadow-xs"
              : "bg-slate-100 border border-slate-200 shadow-inner";

            return (
              <div className={containerStyle}>
                <div className="space-y-2 flex flex-col items-center">
                  <div className="flex items-center justify-center gap-2">
                    <span
                      className={`h-3 w-3 rounded-full ${viewingNextShift ? "bg-amber-500" : "bg-emerald-500 animate-pulse"}`}
                    ></span>
                    <h3
                      className={`text-base md:text-lg uppercase font-black tracking-widest font-mono ${viewingNextShift ? "text-amber-600" : "text-[#10b981]"}`}
                    >
                      {viewingNextShift
                        ? "Siguiente Turno"
                        : "Turno Activo Ahora"}
                    </h3>
                  </div>
                  <p className="text-xs text-slate-500 max-w-md">
                    El cambio ocurre de forma automática todos los días a las
                    08:30 AM.
                  </p>
                </div>

                {/* TIME BAR & CLOCK GRID CONTAINER */}
                <div className="w-full flex flex-col md:flex-row items-center justify-center gap-6 max-w-2xl">
                  {/* CURRENT CLOCK */}
                  <div className="flex flex-col items-center gap-1.5 w-full md:w-auto">
                    <div className="text-[10px] text-slate-400 uppercase font-extrabold tracking-widest font-mono">
                      Hora Actual
                    </div>
                    <div
                      className={`flex items-center gap-2 px-6 py-2.5 rounded-xl ${itemBg}`}
                    >
                      <Clock size={16} className="text-slate-400 shrink-0" />
                      <span className="font-mono text-2xl md:text-3xl font-extrabold text-[#111827] tracking-wider">
                        {currentClockTime || "08:30:00"}
                      </span>
                    </div>
                  </div>

                  <div className="hidden md:block h-8 w-px bg-slate-200" />

                  {/* COUNTDOWN */}
                  <div className="flex flex-col items-center gap-1.5 w-full md:w-auto">
                    <div className="text-[10px] text-slate-400 uppercase font-extrabold tracking-widest font-mono">
                      Tiempo Restante en Guardia
                    </div>
                    <div className="inline-flex gap-2 justify-center">
                      <div
                        className={`rounded-xl px-4 py-2 min-w-[70px] text-center ${itemBg}`}
                      >
                        <span className="block text-2xl md:text-3xl font-mono font-bold text-slate-800 tracking-tight font-sans">
                          {String(timeLeft.hours).padStart(2, "0")}
                        </span>
                        <span className="text-[9px] uppercase font-bold text-slate-500 font-mono">
                          hs
                        </span>
                      </div>
                      <div
                        className={`rounded-xl px-4 py-2 min-w-[70px] text-center ${itemBg}`}
                      >
                        <span className="block text-2xl md:text-3xl font-mono font-bold text-slate-800 tracking-tight font-sans">
                          {String(timeLeft.minutes).padStart(2, "0")}
                        </span>
                        <span className="text-[9px] uppercase font-bold text-slate-500 font-mono">
                          min
                        </span>
                      </div>
                      <div
                        className={`rounded-xl px-4 py-2 min-w-[70px] text-center ${itemBg}`}
                      >
                        <span className="block text-2xl md:text-3xl font-mono font-bold text-emerald-600 tracking-tight font-sans">
                          {String(timeLeft.seconds).padStart(2, "0")}
                        </span>
                        <span className="text-[9px] uppercase font-bold text-slate-500 font-mono">
                          seg
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          };

          const renderPromoModule = () => {
            if (
              !frontpageConfig.promoModuleEnabled ||
              (!frontpageConfig.promoModuleImage &&
                !frontpageConfig.promoModuleDesktopImage)
            ) {
              return null;
            }

            const mobileImg = frontpageConfig.promoModuleImage;
            const desktopImg = frontpageConfig.promoModuleDesktopImage;
            const altText =
              frontpageConfig.promoModuleAlt || "Módulo publicitario";

            const card = (
              <div className="w-full flex justify-center items-center">
                {desktopImg && mobileImg ? (
                  <div className="w-full">
                    {/* Mobile Display (320x720 o formato vertical) */}
                    <div className="block md:hidden w-full max-w-md mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xs bg-white group transition-all duration-300 hover:shadow-md">
                      <img
                        src={mobileImg}
                        alt={altText}
                        className="w-full h-auto max-h-[720px] object-contain rounded-2xl block select-none mx-auto"
                        referrerPolicy="no-referrer"
                        loading="lazy"
                      />
                    </div>
                    {/* Desktop Display (1230x140 o formato horizontal) */}
                    <div className="hidden md:flex w-full max-w-7xl mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xs bg-white justify-center items-center group transition-all duration-300 hover:shadow-md">
                      <img
                        src={desktopImg}
                        alt={altText}
                        className="w-full h-auto max-h-[220px] object-contain rounded-2xl block select-none mx-auto"
                        referrerPolicy="no-referrer"
                        loading="lazy"
                      />
                    </div>
                  </div>
                ) : desktopImg ? (
                  <div className="w-full max-w-7xl mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xs bg-white flex justify-center items-center group transition-all duration-300 hover:shadow-md">
                    <img
                      src={desktopImg}
                      alt={altText}
                      className="w-full h-auto max-h-[220px] object-contain rounded-2xl block select-none mx-auto"
                      referrerPolicy="no-referrer"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="w-full max-w-md mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xs bg-white flex flex-col justify-center items-center group transition-all duration-300 hover:shadow-md">
                    <img
                      src={mobileImg}
                      alt={altText}
                      className="w-full h-auto max-h-[720px] object-contain rounded-2xl block select-none"
                      referrerPolicy="no-referrer"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            );

            return (
              <div className="w-full flex justify-center my-2 md:my-8 md:py-2">
                {frontpageConfig.promoModuleLink ? (
                  <a
                    href={frontpageConfig.promoModuleLink}
                    target="_blank"
                    rel="noreferrer"
                    className="block w-full max-w-7xl cursor-pointer no-underline focus:outline-hidden"
                  >
                    {card}
                  </a>
                ) : (
                  card
                )}
              </div>
            );
          };

          return (
            <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col justify-between font-sans selection:bg-emerald-500 selection:text-white pb-0">
              <div>
                {/* 1. TOP BANNER */}
                {renderTopBanner()}

                {/* 2. SECOND BANNER */}
                {renderSecondBanner()}

                {/* 3. WHATSAPP BANNER AT TOP POSITION */}
                {(frontpageConfig.whatsAppBannerPosition === "top" ||
                  frontpageConfig.whatsAppBannerPosition === "superior") && (
                  <div className="pt-2 md:pt-4">
                    {renderWhatsAppPromoBanner()}
                  </div>
                )}

                {/* PUBLIC WORKSPACE CONTAINER */}
                <main className="max-w-7xl mx-auto px-4 pt-3 pb-8 md:pt-6 md:pb-8 space-y-6 md:space-y-8">
                  {/* CURRENT DATE SHIFT HEADER & COUNTDOWN (UPPER PLACEHOLDER) */}
                  {!frontpageConfig.countdownBelowToggle &&
                    renderCountdownCard()}

                  {/* 🧭 CLOSEST PHARMACY LOCATOR CONTROL BAR */}
                  {frontpageConfig.locationBoxPosition !== "below" && (
                    <div className="space-y-4">
                      {frontpageConfig.promoModulePosition === "above_location" &&
                        renderPromoModule()}
                      {renderLocationLocator()}
                      {frontpageConfig.promoModulePosition === "below_location" &&
                        renderPromoModule()}

                      {/* GEOLOCATION ERROR MESSAGE CONTAINER */}
                      {locationError && (
                        <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-xl flex items-start justify-between gap-3 shadow-xs">
                          <div className="flex items-start gap-3">
                            <AlertCircle
                              className="text-rose-600 shrink-0 mt-0.5"
                              size={16}
                            />
                            <div className="space-y-1">
                              <p className="text-xs font-semibold">
                                {locationError}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setLocationError(null)}
                            className="text-rose-500 hover:text-rose-700 font-mono text-xs font-bold leading-none cursor-pointer"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* PROMO MODULE WHEN POSITIONED ABOVE PHARMACIES */}
                  {frontpageConfig.promoModulePosition === "above_pharmacies" &&
                    renderPromoModule()}

                  {/* NEXT SHIFT WARNING NOTICE IF ACTIVE */}
                  {viewingNextShift && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl flex items-start gap-4 shadow-xs">
                      <Info
                        className="text-amber-600 shrink-0 mt-0.5"
                        size={16}
                      />
                      <div className="space-y-1">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-amber-900">
                          Aviso: Farmacias del Próximo Turno
                        </h4>
                        <p className="text-xs text-slate-600">
                          Estás consultando los locales que estarán de guardia
                          mañana, de <b>8:30 AM</b> a <b>8:30 AM</b> del día
                          siguiente.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* PHARMACIES RENDERING GRID OR CAROUSEL */}
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3">
                      <RefreshCw
                        className="animate-spin text-emerald-500"
                        size={32}
                      />
                      <p className="text-xs text-slate-400 font-mono">
                        Cargando farmacias de Tandil...
                      </p>
                    </div>
                  ) : (viewingNextShift
                      ? shifts.find(
                          (s) => s.date === getActiveShiftDateClient(1),
                        )?.pharmacyIds || []
                      : shifts.find(
                          (s) => s.date === getActiveShiftDateClient(0),
                        )?.pharmacyIds || []
                    ).length === 0 ? (
                    <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-xs">
                      <AlertCircle
                        className="mx-auto text-amber-500 mb-3 animate-pulse"
                        size={36}
                      />
                      <h3 className="text-sm font-bold text-slate-800">
                        Sin Guardia Registrada
                      </h3>
                      <p className="text-xs text-slate-500 mt-1.5 max-w-md mx-auto leading-relaxed">
                        Lo siento, nuestro cronograma del mes no contiene turnos
                        registrados para esa fecha. Vuelve a consultar más tarde
                        o contacta al administrador.
                      </p>
                    </div>
                  ) : (
                    (() => {
                      const activePharmIds = viewingNextShift
                        ? shifts.find(
                            (s) => s.date === getActiveShiftDateClient(1),
                          )?.pharmacyIds || []
                        : shifts.find(
                            (s) => s.date === getActiveShiftDateClient(0),
                          )?.pharmacyIds || [];

                      let activePharmList = pharmacies.filter((p) =>
                        activePharmIds.includes(p.id),
                      );

                      if (userLocation) {
                        activePharmList = [...activePharmList].sort((a, b) => {
                          const distA =
                            distanceByPharmId[a.id] !== undefined
                              ? distanceByPharmId[a.id]
                              : Infinity;
                          const distB =
                            distanceByPharmId[b.id] !== undefined
                              ? distanceByPharmId[b.id]
                              : Infinity;
                          return distA - distB;
                        });
                      }

                      // Identificamos la farmacia más cercana y la más lejana de las que se calculó la distancia
                      const validDistancePharms = activePharmList.filter(
                        (p) => distanceByPharmId[p.id] !== undefined,
                      );
                      const closestPharmId =
                        validDistancePharms.length > 0
                          ? validDistancePharms[0].id
                          : null;
                      const farthestPharmId =
                        validDistancePharms.length > 1
                          ? validDistancePharms[validDistancePharms.length - 1]
                              .id
                          : null;

                      const renderPharmacyCard = (pharm: Pharmacy) => {
                        const fallbackBg =
                          "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80";
                        const bgPhoto = pharm.fotoUrl || fallbackBg;
                        const showPhotoOnMobile =
                          frontpageConfig.showPhotoOnMobile !== false;

                        return (
                          <div
                            key={pharm.id}
                            className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col shadow-xs h-full"
                          >
                            {/* Box 1: Photo Background with White name superimposed */}
                            <div
                              style={{ backgroundImage: `url(${bgPhoto})` }}
                              className={`h-44 bg-cover bg-center relative w-full items-center justify-center shrink-0 ${showPhotoOnMobile ? "flex" : "hidden md:flex"}`}
                            >
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10" />
                              <div className="relative z-10 text-center px-4 w-full">
                                <h2 className="text-2xl font-black uppercase tracking-wider text-white select-all text-shadow notranslate" translate="no">
                                  {pharm.nombre}
                                </h2>
                              </div>
                            </div>

                            {/* Box 2: Detail Box in configurable color */}
                            <div
                              style={{ backgroundColor: detailsColor }}
                              className="p-5 flex-1 flex flex-col justify-between"
                            >
                              <div className="space-y-4">
                                {/* Mobile title when photo is hidden */}
                                {!showPhotoOnMobile && (
                                  <div className="md:hidden border-b border-white/20 pb-2 mb-2">
                                    <h2 className="text-xl font-extrabold uppercase tracking-wider text-white select-all notranslate" translate="no">
                                      {pharm.nombre}
                                    </h2>
                                  </div>
                                )}

                                {/* Phone with Phone icon (now first and centered) */}
                                <div className="flex flex-col items-center justify-center text-center">
                                  <a
                                    href={
                                      pharm.whatsapp
                                        ? `https://wa.me/${sanitizePhoneNumber(pharm.whatsapp)}?text=${encodeURIComponent(`Hola Farmacia ${pharm.nombre}, vi que están de turno hoy en Tandil y quería realizar una consulta.`)}`
                                        : `tel:${pharm.telefono ? String(pharm.telefono).replace(/[^0-9+]/g, "") : ""}`
                                    }
                                    target={
                                      pharm.whatsapp ? "_blank" : undefined
                                    }
                                    rel="noreferrer"
                                    className={`inline-flex items-center gap-2 justify-center hover:opacity-85 transition-opacity ${phoneFontSizeClass} ${phoneFontWeightClass} ${textPhoneColorClass}`}
                                  >
                                    <Phone className="shrink-0" size={15} />
                                    <span>{pharm.telefono}</span>
                                  </a>
                                </div>

                                {/* Address with MapPin icon (below phone, centered, clickable to Google Maps) */}
                                <div className="flex flex-col items-center justify-center text-center mt-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const url = userLocation
                                        ? `https://www.google.com/maps/dir/?api=1&origin=${userLocation.lat},${userLocation.lng}&destination=Farmacia+${encodeURIComponent(pharm.nombre)}+${encodeURIComponent(pharm.direccion)}+Tandil`
                                        : pharm.locationUrl ||
                                          `https://www.google.com/maps/search/?api=1&query=Farmacia+${encodeURIComponent(pharm.nombre)}+${encodeURIComponent(pharm.direccion)}+Tandil`;
                                      window.open(url, "_blank");
                                    }}
                                    className={`inline-flex items-center gap-2 justify-center hover:opacity-85 cursor-pointer transition-opacity ${addressFontSizeClass} ${addressFontWeightClass} ${addressFontColorClass} border-none bg-transparent p-0 outline-none`}
                                  >
                                    <MapPin className="shrink-0" size={15} />
                                    <span>{pharm.direccion}</span>
                                  </button>

                                  {/* Distancia real en Km o metros con tags especiales de cercanía */}
                                  {distanceByPharmId[pharm.id] !==
                                    undefined && (
                                    <div className="mt-3 flex flex-col items-center gap-1.5 select-none w-full">
                                      {pharm.id === closestPharmId && (
                                        <span className="bg-emerald-500 text-slate-950 text-[10px] font-black uppercase tracking-wider px-3 py-0.5 rounded-full shadow-xs animate-pulse font-sans flex items-center gap-1 border border-emerald-450">
                                          <span>📍</span> Más cercana
                                        </span>
                                      )}
                                      {pharm.id === farthestPharmId && (
                                        <span className="bg-amber-400 text-slate-950 text-[10px] font-black uppercase tracking-wider px-3 py-0.5 rounded-full shadow-xs font-sans flex items-center gap-1 border border-amber-300">
                                          <span>📍</span> Más lejana
                                        </span>
                                      )}

                                      <div className="text-xs font-black text-white bg-slate-950/45 border border-white/20 backdrop-blur-md py-1.5 px-4 rounded-full inline-flex items-center gap-1 font-mono hover:scale-105 transition-all shadow-xs shrink-0 select-all">
                                        {distanceByPharmId[pharm.id] < 1 ? (
                                          <span>
                                            A{" "}
                                            {Math.round(
                                              distanceByPharmId[pharm.id] *
                                                1000,
                                            )}{" "}
                                            metros
                                          </span>
                                        ) : (
                                          <span>
                                            A {distanceByPharmId[pharm.id]} km
                                            de distancia
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="flex gap-2 mt-5 pt-4 border-t border-white/20">
                                {/* Google Maps Trigger */}
                                <button
                                  onClick={() => {
                                    const url = userLocation
                                      ? `https://www.google.com/maps/dir/?api=1&origin=${userLocation.lat},${userLocation.lng}&destination=Farmacia+${encodeURIComponent(pharm.nombre)}+${encodeURIComponent(pharm.direccion)}+Tandil`
                                      : pharm.locationUrl ||
                                        `https://www.google.com/maps/search/?api=1&query=Farmacia+${encodeURIComponent(pharm.nombre)}+${encodeURIComponent(pharm.direccion)}+Tandil`;
                                    window.open(url, "_blank");
                                  }}
                                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-white/10 hover:bg-white/20 active:scale-95 text-white rounded-lg text-xs font-bold transition-all shadow-xs border border-white/5 cursor-pointer"
                                >
                                  <MapPin size={12} />
                                  {userLocation
                                    ? "¿Cómo llegar?"
                                    : "Google Maps"}
                                </button>

                                {/* Optional WhatsApp Trigger */}
                                {pharm.whatsapp && (
                                  <button
                                    onClick={() => {
                                      window.open(
                                        `https://wa.me/${sanitizePhoneNumber(pharm.whatsapp)}?text=${encodeURIComponent(`Hola Farmacia ${pharm.nombre}, vi que están de turno hoy en Tandil y quería realizar una consulta.`)}`,
                                        "_blank",
                                      );
                                    }}
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white rounded-lg text-xs font-bold transition-all shadow-xs cursor-pointer"
                                  >
                                    <MessageSquare size={12} />
                                    WhatsApp
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      };

                      const configuredPerPage =
                        frontpageConfig.pharmaciesPerRow || 2;
                      let dynamicPerPage = configuredPerPage;
                      if (windowWidth >= 768 && windowWidth < 1024) {
                        // El formato tablet de pantallas md (768px a 1023px) puede mostrar como máximo 2 farmacias por fila cleanly.
                        // Si hay más farmacias activas que el límite de 1 fila (máx 2), activamos el carrusel para evitar saltos de línea.
                        dynamicPerPage = Math.min(configuredPerPage, 2);
                      } else if (windowWidth < 768) {
                        dynamicPerPage = 1;
                      }

                      const useCarousel =
                        frontpageConfig.carouselDesktop ||
                        activePharmList.length > dynamicPerPage;

                      if (useCarousel) {
                        const maxStart = Math.max(
                          0,
                          activePharmList.length - dynamicPerPage,
                        );
                        const safeStartIndex = Math.min(
                          carouselStartIndex,
                          maxStart,
                        );
                        const visiblePharmacies = activePharmList.slice(
                          safeStartIndex,
                          safeStartIndex + dynamicPerPage,
                        );

                        return (
                          <div>
                            {/* Desktop View: Slide Carousel */}
                            <div className="hidden md:block relative px-10">
                              {activePharmList.length > dynamicPerPage && (
                                <>
                                  {/* Left Arrow Button (Infinite loop) */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCarouselDirection("prev");
                                      setCarouselStartIndex((prev) =>
                                        prev === 0 ? maxStart : prev - 1,
                                      );
                                    }}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 z-20 h-10 w-10 flex items-center justify-center rounded-full bg-white border border-slate-200 shadow-md text-slate-700 hover:bg-slate-50 transition-all cursor-pointer"
                                  >
                                    <ChevronLeft size={20} />
                                  </button>

                                  {/* Right Arrow Button (Infinite loop) */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCarouselDirection("next");
                                      setCarouselStartIndex((prev) =>
                                        prev >= maxStart ? 0 : prev + 1,
                                      );
                                    }}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 z-20 h-10 w-10 flex items-center justify-center rounded-full bg-white border border-slate-200 shadow-md text-slate-700 hover:bg-slate-50 transition-all cursor-pointer"
                                  >
                                    <ChevronRight size={20} />
                                  </button>
                                </>
                              )}

                              <AnimatePresence mode="wait">
                                <motion.div
                                  key={`${safeStartIndex}-${viewingNextShift}`}
                                  initial={{
                                    opacity: 0,
                                    x: carouselDirection === "next" ? 60 : -60,
                                  }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{
                                    opacity: 0,
                                    x: carouselDirection === "next" ? -60 : 60,
                                  }}
                                  transition={{
                                    duration: 0.35,
                                    ease: [0.25, 1, 0.5, 1],
                                  }}
                                  className="flex flex-wrap justify-center gap-6"
                                >
                                  {visiblePharmacies.map((p) => {
                                    const widthClass =
                                      dynamicPerPage === 1
                                        ? "w-full max-w-xl"
                                        : dynamicPerPage === 2
                                          ? "w-full md:w-[calc(50%-12px)]"
                                          : dynamicPerPage === 3
                                            ? "w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
                                            : "w-full md:w-[calc(33.333%-16px)] lg:w-[calc(25%-18px)]";
                                    return (
                                      <div
                                        key={p.id}
                                        className={`${widthClass} h-stretch`}
                                      >
                                        {renderPharmacyCard(p)}
                                      </div>
                                    );
                                  })}
                                </motion.div>
                              </AnimatePresence>

                              {/* Dots / Indicators */}
                              {activePharmList.length > dynamicPerPage && (
                                <div className="flex justify-center gap-1.5 mt-6">
                                  {Array.from({
                                    length:
                                      activePharmList.length -
                                      dynamicPerPage +
                                      1,
                                  }).map((_, idx) => (
                                    <button
                                      key={idx}
                                      type="button"
                                      onClick={() => {
                                        setCarouselDirection(
                                          idx > safeStartIndex
                                            ? "next"
                                            : "prev",
                                        );
                                        setCarouselStartIndex(idx);
                                      }}
                                      className={`h-2 rounded-full transition-all duration-300 ${safeStartIndex === idx ? "w-5 bg-emerald-500" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Mobile View: Standard stacked scroll list (one beneath another) as requested */}
                            <div className="md:hidden flex flex-col gap-6">
                              {activePharmList.map((p) =>
                                renderPharmacyCard(p),
                              )}
                            </div>
                          </div>
                        );
                      }

                      // Standard static grid column layout
                      const columns = frontpageConfig.pharmaciesPerRow || 2;
                      if (activePharmList.length < columns) {
                        return (
                          <div className="flex flex-wrap justify-center gap-6">
                            {activePharmList.map((p) => {
                              const widthClass =
                                columns === 1
                                  ? "w-full max-w-xl"
                                  : columns === 2
                                    ? "w-full md:w-[calc(50%-12px)] max-w-md"
                                    : columns === 3
                                      ? "w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] max-w-sm"
                                      : "w-full md:w-[calc(33.333%-16px)] lg:w-[calc(25%-18px)] max-w-[280px]";
                              return (
                                <div
                                  key={p.id}
                                  className={`${widthClass} h-stretch`}
                                >
                                  {renderPharmacyCard(p)}
                                </div>
                              );
                            })}
                          </div>
                        );
                      }

                      // Standard static grid column layout
                      return (
                        <div className={`grid ${gridColsClass} gap-6`}>
                          {activePharmList.map((p) => renderPharmacyCard(p))}
                        </div>
                      );
                    })()
                  )}

                  {/* PROMO MODULE WHEN POSITIONED BELOW PHARMACIES */}
                  {frontpageConfig.promoModulePosition === "below_pharmacies" &&
                    renderPromoModule()}

                  {/* 🧭 CLOSEST PHARMACY LOCATOR CONTROL BAR (LOWER PLACEHOLDER) */}
                  {frontpageConfig.locationBoxPosition === "below" && (
                    <div className="pt-4 border-t border-slate-200 mt-4 space-y-4">
                      {frontpageConfig.promoModulePosition === "above_location" &&
                        renderPromoModule()}
                      {renderLocationLocator()}
                      {frontpageConfig.promoModulePosition === "below_location" &&
                        renderPromoModule()}

                      {/* GEOLOCATION ERROR MESSAGE CONTAINER */}
                      {locationError && (
                        <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-xl flex items-start justify-between gap-3 shadow-xs">
                          <div className="flex items-start gap-3">
                            <AlertCircle
                              className="text-rose-600 shrink-0 mt-0.5"
                              size={16}
                            />
                            <div className="space-y-1">
                              <p className="text-xs font-semibold">
                                {locationError}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setLocationError(null)}
                            className="text-rose-500 hover:text-rose-700 font-mono text-xs font-bold leading-none cursor-pointer"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* NEXT / CURRENT TOGGLE BUTTON IN COMMUNITY VIEW */}
                  {/* NEXT / CURRENT TOGGLE BUTTON IN COMMUNITY VIEW WITH BELOW COUNTDOWN SUPPORT */}
                  <div className="flex flex-col items-center gap-6 pt-8 border-t border-slate-200">
                    <div className="flex justify-center">
                      {!viewingNextShift ? (
                        <button
                          onClick={() => setViewingNextShift(true)}
                          className="px-6 py-3 border border-slate-200 bg-white hover:bg-slate-50 text-emerald-600 hover:text-emerald-700 text-xs font-extrabold rounded-xl transition-all shadow-sm flex items-center gap-2 cursor-pointer"
                        >
                          <CalendarIcon size={14} />
                          Ver farmacias del siguiente turno
                        </button>
                      ) : (
                        <button
                          onClick={() => setViewingNextShift(false)}
                          className="px-6 py-3 border border-slate-200 bg-white hover:bg-slate-50 text-blue-600 hover:text-blue-700 text-xs font-extrabold rounded-xl transition-all shadow-sm flex items-center gap-2 cursor-pointer"
                        >
                          <RefreshCw size={14} />
                          Volver al turno de hoy
                        </button>
                      )}
                    </div>

                    {/* Countdown Card placed here conditionally */}
                    {frontpageConfig.countdownBelowToggle && (
                      <div className="w-full max-w-3xl mt-2">
                        {renderCountdownCard()}
                      </div>
                    )}
                  </div>
                </main>

                {/* 4. WHATSAPP BANNER AT BOTTOM POSITION */}
                {(frontpageConfig.whatsAppBannerPosition === "bottom" ||
                  frontpageConfig.whatsAppBannerPosition === "inferior") && (
                  <>
                    <div className="h-6" />
                    {renderWhatsAppPromoBanner()}
                  </>
                )}
              </div>

              {/* COMMUNITY FOOTER & BACKEND LOGIN */}
              <footer
                style={{
                  backgroundColor: frontpageConfig.footerBgColor || "#1e293b",
                }}
                className="py-12 px-6 mt-16 transition-all text-white border-t border-slate-150"
              >
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 items-stretch font-sans text-sm text-white">
                  {/* LADO IZQUIERDO: Logo, Dirección, Teléfono, Email */}
                  <div className="space-y-4 text-left">
                    {frontpageConfig.footerLogoUrl && (
                      <img
                        src={frontpageConfig.footerLogoUrl}
                        alt="Logo de Farmacia"
                        width="200"
                        height="64"
                        className="max-h-16 object-contain"
                        style={{ aspectRatio: "200/64" }}
                        referrerPolicy="no-referrer"
                      />
                    )}
                    <div className="space-y-2 opacity-90 text-xs text-white">
                      {frontpageConfig.footerAddress && (
                        <div className="flex items-center gap-2 text-white">
                          <MapPin
                            size={14}
                            className="shrink-0 text-emerald-400 text-white"
                          />
                          <span className="text-white">
                            {frontpageConfig.footerAddress}
                          </span>
                        </div>
                      )}
                      {frontpageConfig.footerPhone && (
                        <div className="flex items-center gap-2 text-white">
                          <Phone
                            size={14}
                            className="shrink-0 text-emerald-400 text-white"
                          />
                          <span className="text-white">
                            {frontpageConfig.footerPhone}
                          </span>
                        </div>
                      )}
                      {frontpageConfig.footerEmail && (
                        <div className="flex items-center gap-2 text-white">
                          <Mail
                            size={14}
                            className="shrink-0 text-emerald-400 text-white"
                          />
                          <span className="text-white">
                            {frontpageConfig.footerEmail}
                          </span>
                        </div>
                      )}
                      {!frontpageConfig.footerAddress &&
                        !frontpageConfig.footerPhone &&
                        !frontpageConfig.footerEmail && (
                          <p className="text-white/40 italic">
                            Información de contacto no configurada
                          </p>
                        )}
                    </div>
                  </div>

                  {/* CENTRO: Redes Sociales */}
                  <div className="flex flex-col items-center justify-center text-center space-y-4 border-y md:border-y-0 md:border-x border-white/10 py-6 md:py-0 px-4">
                    <h4 className="font-bold text-sm tracking-wider uppercase opacity-80 text-white">
                      {frontpageConfig.footerSocialsText ||
                        "Seguinos en redes sociales"}
                    </h4>
                    <div className="flex items-center gap-4">
                      {frontpageConfig.footerFacebookUrl ? (
                        <a
                          href={frontpageConfig.footerFacebookUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="h-9 w-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 hover:scale-105 transition-all text-white"
                          title="Facebook"
                        >
                          <Facebook size={18} />
                        </a>
                      ) : (
                        <span className="text-xs text-white/40 italic text-white/50">
                          Facebook no configurado
                        </span>
                      )}

                      {frontpageConfig.footerInstagramUrl ? (
                        <a
                          href={frontpageConfig.footerInstagramUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="h-9 w-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 hover:scale-105 transition-all text-white"
                          title="Instagram"
                        >
                          <Instagram size={18} />
                        </a>
                      ) : (
                        <span className="text-xs text-white/40 italic text-white/50">
                          Instagram no configurado
                        </span>
                      )}
                    </div>
                  </div>

                  {/* DERECHA: Derechos */}
                  <div className="flex flex-col items-center md:items-end gap-2 text-center md:text-right">
                    <div className="text-xs opacity-80 text-white">
                      {frontpageConfig.footerCopyrightLink ? (
                        <a
                          href={frontpageConfig.footerCopyrightLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-normal text-white hover:font-bold hover:no-underline whitespace-pre-wrap transition-all block"
                        >
                          {frontpageConfig.footerCopyrightText || `© ${new Date().getFullYear()} FarmaTurno Tandil\nTodos los derechos reservados.`}
                        </a>
                      ) : (
                        <p className="font-normal text-white whitespace-pre-wrap block">
                          {frontpageConfig.footerCopyrightText || `© ${new Date().getFullYear()} FarmaTurno Tandil\nTodos los derechos reservados.`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </footer>

              {!loading && frontpageConfig.showWhatsAppFloatingButton && (
                <a
                  href={globalWaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fixed bottom-6 right-6 w-[60px] h-[60px] bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all z-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
                  aria-label="Contactar por WhatsApp"
                >
                  <svg
                    className="w-8 h-8"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.878-.788-1.47-1.761-1.643-2.061-.173-.298-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                  </svg>
                </a>
              )}
            </div>
          );
        })()}

      {/* Panel de administración (§1, §4): tokens y tema dentro de .admin */}
      {activeTab !== "public" && (
        <div
          className={`admin${adminTheme === "dark" ? " dark" : ""} min-h-screen bg-adm-bg text-adm-text`}
          style={{ colorScheme: adminTheme }}
        >
          <ToastProvider>
        {(() => {
          // Sin sesión de Firebase o sin rol (§4.6) no se ve el panel: se ve el login.
          // Mientras falta la configuración (de ahí sale el admin de turnos), se espera.
          if (!authReady || !currentUser || !adminRole || signingIn) {
            const loginStatus: LoginStatus =
              !authReady || (currentUser && !adminRole && loading)
                ? "checking"
                : signingIn
                  ? "signing-in"
                  : currentUser
                    ? "unauthorized"
                    : loginError
                      ? "error"
                      : "signed-out";
            return (
              <Login
                status={loginStatus}
                errorMessage={loginError}
                onSignIn={handleGoogleSignIn}
              />
            );
          }

          return (
            <AdminShell
              view={activeTab === "public" ? "turnos" : activeTab}
              onNavigate={navigateAdmin}
              role={adminRole ?? "turnos"}
              user={currentUser}
              onLogout={handleAdminLogout}
              unreadEmails={unreadEmails}
              theme={adminTheme}
              onToggleTheme={toggleAdminTheme}
            >
              {activeTab === "cfg:plantillas" && (
                <ConfigPage view="cfg:plantillas" role="super" onNavigate={navigateAdmin}>
                  <PlantillasPage />
                </ConfigPage>
              )}
              {activeTab === "cfg:accesos" && (
                <AccesosPage
                  config={frontpageConfig}
                  superAdminEmail={currentUser?.email || ""}
                  loading={loading}
                  loadFailed={frontpageLoadFailed}
                  onReload={fetchData}
                  onNavigate={navigateAdmin}
                  onSaved={setFrontpageConfig}
                />
              )}
                {/* Outer Dashboard layout split */}
                <div className="w-full">
                  {/* Main Workspace */}
                  <div className="w-full space-y-6">
                    {/* 1. TURNOS (src/admin/TurnosPage.tsx) */}
                    {activeTab === "turnos" && (
                      <TurnosPage
                        pharmacies={pharmacies}
                        shifts={shifts}
                        year={currentYear}
                        month={currentMonth}
                        onPrevMonth={handlePrevMonth}
                        onNextMonth={handleNextMonth}
                        loading={loading && shifts.length === 0 && !shiftsLoadFailed}
                        loadFailed={shiftsLoadFailed}
                        onRetry={fetchData}
                        onRefresh={async () => {
                          // «Actualizar»: regenera la caché pública (Ficha 3) y vuelve a traer los datos
                          await regenerateCache();
                          if (!(await fetchData())) {
                            throw new Error("La web quedó al día, pero no pudimos traer los turnos al panel. Reintentá.");
                          }
                        }}
                        selectedDate={selectedDate}
                        onSelectDate={setSelectedDate}
                        onAddPharmacy={handleAddPharmacyToDate}
                        onRemovePharmacy={handleRemovePharmacyFromDate}
                        onClearMonth={handleClearMonth}
                        uploadOpen={showOriginalExcelUploader}
                        onUploadOpenChange={setShowOriginalExcelUploader}
                        onUploaded={fetchData}
                      />
                    )}

                    {activeTab === "cfg:farmacias" && (
                      <FarmaciasPage
                        pharmacies={pharmacies}
                        loading={loading}
                        loadFailed={shiftsLoadFailed}
                        role={adminRole ?? "turnos"}
                        onNavigate={navigateAdmin}
                        onReload={fetchData}
                      />
                    )}
                    {activeTab === "cfg:bot" && (
                      <BotPage diag={diagData} diagError={diagError} onReload={loadDiag} onNavigate={navigateAdmin} />
                    )}

                    {activeTab === "carteles" && (
                      <CartelesPage shifts={shifts} pharmacies={pharmacies} onNavigate={navigateAdmin} />
                    )}
                    {activeTab === "cfg:correos" && (
                      <ConfigPage view="cfg:correos" role="super" onNavigate={navigateAdmin}>
                        <EmailInbox onUnreadChange={setUnreadEmails} />
                      </ConfigPage>
                    )}

                    {activeTab === "cfg:portada" && (
                      <PortadaPage
                        config={frontpageConfig}
                        loading={loading}
                        loadFailed={frontpageLoadFailed}
                        onReload={fetchData}
                        onNavigate={navigateAdmin}
                        onSaved={setFrontpageConfig}
                        onDirtyChange={setPortadaDirty}
                      />
                    )}
                  </div>
                </div>
            </AdminShell>
          );
        })()}
          </ToastProvider>
        </div>
      )}

    </div>
  );
}
